import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faPlay } from "@fortawesome/pro-solid-svg-icons/faPlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import {
  EntityType,
  EntityTypesSupportingFieldCustomization,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout, { ChildLayout } from "@mapmycustomers/shared/types/layout/FormLayout";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import CreateActivityModal from "@app/component/createEditEntity/Activity/CreateActivityModal";
import CreateCompanyModal from "@app/component/createEditEntity/CreateCompanyModal";
import CreateDealModal from "@app/component/createEditEntity/CreateDealModal";
import CreatePersonModal from "@app/component/createEditEntity/CreatePersonModal";
import styles from "@app/scene/settings/component/FormLayouts/component/LayoutModal/LayoutModal.module.scss";
import {
  getActiveEntityType,
  getEditedChildLayoutId,
  getEditedLayout,
} from "@app/scene/settings/component/FormLayouts/store";
import { getActivityTypes } from "@app/store/activity";
import { getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import ActivityTypeWarning from "./ActivityTypeWarning";
import DealStageWarning from "./DealStageWarning";

const messages = defineMessages({
  preview: {
    id: "settings.formLayouts.modal.action.preview",
    defaultMessage: "Preview",
    description: "Label for Preview action button",
  },
});

interface Props {
  activeChildLayout?: ChildLayout["id"];
  disabled?: boolean;
  entityType: EntityTypesSupportingFieldCustomization;
  funnelStages: Record<Funnel["id"], Stage[]>;
  layout?: FormLayout;
}

const PreviewButton: React.FC<Props> = ({
  activeChildLayout,
  disabled,
  entityType,
  funnelStages,
  layout,
}) => {
  const analyticsIssuer = useAnalytics();
  const intl = useIntl();

  const [previewVisible, showPreview, hidePreview] = useBoolean();

  const handlePreview = useCallback(() => {
    analyticsIssuer.clicked(["Preview"]);
    showPreview();
  }, [analyticsIssuer, showPreview]);

  const childLayout = useMemo(
    () => (layout?.childLayouts ?? []).find((child) => child.id === activeChildLayout),
    [activeChildLayout, layout]
  );

  const defaultActivityTypeId = useMemo(() => {
    if (entityType !== EntityType.ACTIVITY || activeChildLayout) {
      return undefined;
    }
    return layout?.schema.find((schemaField) => schemaField.field === "crmActivityTypeId")
      ?.defaultValue?.[0] as ActivityType["id"];
  }, [activeChildLayout, entityType, layout]);

  const [defaultFunnelId, defaultStageId] = useMemo(() => {
    if (entityType !== EntityType.DEAL || activeChildLayout) {
      return [undefined, undefined];
    }
    let funnelId: Funnel["id"] | undefined;
    let stageId: Stage["id"] | undefined;
    layout?.schema.forEach((schemaField) => {
      if (schemaField.field === "funnelId") {
        funnelId = schemaField.defaultValue?.[0] as Funnel["id"];
      } else if (schemaField.field === "dealStageId") {
        const defaultStageId = schemaField.defaultValue?.[0] as Stage["id"];
        const funnelIdString = Object.keys(funnelStages).find((funnelId) =>
          funnelStages[parseInt(funnelId) as Funnel["id"]].some(({ id }) => id === defaultStageId)
        );
        if (defaultStageId && funnelIdString) {
          stageId = defaultStageId;
          funnelId = parseInt(funnelIdString);
        }
      }
    });
    return [funnelId, stageId];
  }, [activeChildLayout, entityType, funnelStages, layout]);

  const activityTypeId: ActivityType["id"] | undefined = useMemo(() => {
    // nothing to return if it's not an activity layout
    if (entityType !== EntityType.ACTIVITY) {
      return undefined;
    }

    // return activity type for given variant if previewing a variant
    if (childLayout) {
      return childLayout.crmActivityType?.id;
    }

    return defaultActivityTypeId;
  }, [childLayout, defaultActivityTypeId, entityType]);

  const [funnelId, stageId]: [Funnel["id"] | undefined, Stage["id"] | undefined] = useMemo(() => {
    // nothing to return if it's not a deal layout
    if (entityType !== EntityType.DEAL) {
      return [undefined, undefined];
    }

    // return funnel/stage for given variant if previewing a variant
    if (childLayout) {
      return childLayout.funnel && childLayout.stage
        ? [childLayout.funnel.id, childLayout.stage.id]
        : [undefined, undefined]; // should not happen, but let's have it just in case
    }
    return [defaultFunnelId, defaultStageId];
  }, [childLayout, defaultFunnelId, defaultStageId, entityType]);

  const [shouldShowPreviewWarning, variantStageName] = useMemo(() => {
    if (entityType === EntityType.ACTIVITY && !childLayout) {
      return [
        (layout?.childLayouts ?? []).some(
          ({ crmActivityType }) => crmActivityType?.id === defaultActivityTypeId
        ),
        undefined,
      ];
    } else if (entityType === EntityType.DEAL && !childLayout && defaultStageId) {
      const childLayoutForDefaultStage = (layout?.childLayouts ?? []).find(
        ({ stage }) => stage?.id === defaultStageId
      );
      return [!!childLayoutForDefaultStage, childLayoutForDefaultStage?.stage?.name];
    }
    return [false, undefined];
  }, [childLayout, entityType, layout, defaultStageId, defaultActivityTypeId]);

  return (
    <>
      <Button
        className={styles.previewBtn}
        disabled={disabled}
        leftIcon={<FontAwesomeIcon icon={faPlay} />}
        onClick={handlePreview}
      >
        <span>{intl.formatMessage(messages.preview)}</span>
      </Button>

      {previewVisible &&
        (entityType === EntityType.COMPANY ? (
          <CreateCompanyModal onHide={hidePreview} previewLayout={layout} />
        ) : entityType === EntityType.PERSON ? (
          <CreatePersonModal onHide={hidePreview} previewLayout={layout} />
        ) : entityType === EntityType.DEAL ? (
          <CreateDealModal
            dealStageExtra={
              shouldShowPreviewWarning && variantStageName ? (
                <DealStageWarning variantStageName={variantStageName} />
              ) : undefined
            }
            defaultFunnelId={funnelId}
            defaultStageId={stageId}
            onHide={hidePreview}
            previewLayout={layout}
          />
        ) : entityType === EntityType.ACTIVITY ? (
          <CreateActivityModal
            activityTypeExtra={
              shouldShowPreviewWarning && defaultActivityTypeId ? (
                <ActivityTypeWarning defaultActivityTypeId={defaultActivityTypeId} />
              ) : undefined
            }
            defaultActivityTypeId={activityTypeId}
            onHide={hidePreview}
            previewLayout={layout}
          />
        ) : null)}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeChildLayout: getEditedChildLayoutId(state),
  activityTypes: getActivityTypes(state),
  entityType: getActiveEntityType(state),
  funnelStages: getFunnelStages(state),
  layout: getEditedLayout(state),
});

export default connect(mapStateToProps)(PreviewButton);
